import { useState, useEffect } from 'react';
import { useSettings } from '@backpackjs/storefront';
import { Box } from 'theme-ui';
import { themed } from './ApplePay.theme';

const STOREFRONT_API_URL = `https://${process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN}/api/2024-01/graphql.json`;
const STOREFRONT_TOKEN =
  process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN;

const CHECKOUT_CREATE = `
  mutation checkoutCreate($input: CheckoutCreateInput!) {
    checkoutCreate(input: $input) {
      checkout { id webUrl totalPriceV2 { amount currencyCode } }
      checkoutUserErrors { field message }
    }
  }
`;

const CHECKOUT_COMPLETE_TOKENIZED = `
  mutation checkoutCompleteWithTokenizedPaymentV3(
    $checkoutId: ID!
    $payment: TokenizedPaymentInputV3!
  ) {
    checkoutCompleteWithTokenizedPaymentV3(
      checkoutId: $checkoutId
      payment: $payment
    ) {
      checkout { id order { id } }
      checkoutUserErrors { field message }
    }
  }
`;

async function storefrontFetch(query, variables) {
  const res = await fetch(STOREFRONT_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  return res.json();
}

function formatAddress(name, details) {
  const addr = details?.billingAddress;
  if (!addr) return {};
  return {
    firstName: name?.split(' ')[0] ?? '',
    lastName: name?.split(' ').slice(1).join(' ') ?? '',
    address1: addr.addressLine?.[0] ?? '',
    address2: addr.addressLine?.[1] ?? '',
    city: addr.city ?? '',
    province: addr.region ?? '',
    zip: addr.postalCode ?? '',
    countryCode: addr.country ?? 'US',
    phone: addr.phone ?? '',
  };
}

export const ApplePay = themed(({ theme, selectedVariant, quantity = 1 }) => {
  const settings = useSettings();
  const enableApplePay = settings?.product?.applePay?.enable || false;
  const {
    id: variantId,
    priceV2: { amount: price },
    currencyCode,
  } = selectedVariant;
  const { product } = selectedVariant;
  const productTitle = product?.title;
  const [supported, setSupported] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.ApplePaySession &&
      ApplePaySession.canMakePayments()
    ) {
      setSupported(true);
    }
  }, []);

  if (!supported || !enableApplePay) return null;

  async function handleApplePay() {
    setLoading(true);
    let paymentResponse;
    try {
      const paymentRequest = new PaymentRequest(
        [
          {
            supportedMethods: 'https://apple.com/apple-pay',
            data: {
              version: 3,
              merchantIdentifier: process.env.NEXT_PUBLIC_APPLE_PAY_MERCHANT_ID,
              merchantCapabilities: ['supports3DS'],
              supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
              countryCode: 'US',
            },
          },
        ],
        {
          total: {
            label: productTitle,
            amount: {
              currency: currencyCode,
              value: price,
            },
          },
        }
      );

      paymentResponse = await paymentRequest.show();

      const { data: checkoutData } = await storefrontFetch(CHECKOUT_CREATE, {
        input: {
          lineItems: [{ variantId, quantity }],
        },
      });

      const { checkout, checkoutUserErrors } = checkoutData.checkoutCreate;
      if (checkoutUserErrors?.length)
        throw new Error(checkoutUserErrors[0].message);

      const { data: completeData } = await storefrontFetch(
        CHECKOUT_COMPLETE_TOKENIZED,
        {
          checkoutId: checkout.id,
          payment: {
            paymentAmount: {
              amount: price,
              currencyCode,
            },
            idempotencyKey: crypto.randomUUID(),
            paymentData: JSON.stringify(paymentResponse.details.token),
            type: 'APPLE_PAY',
            billingAddress: formatAddress(
              paymentResponse.payerName,
              paymentResponse.details
            ),
          },
        }
      );

      const { checkoutUserErrors: completeErrors } =
        completeData.checkoutCompleteWithTokenizedPaymentV3;
      if (completeErrors?.length) throw new Error(completeErrors[0].message);

      await paymentResponse.complete('success');
      window.location.href = checkout.webUrl;
    } catch (err) {
      console.error('Apple Pay error:', err);
      if (paymentResponse) {
        try {
          await paymentResponse.complete('fail');
        } catch (e) {
          // Payment response already completed
        }
      }
      setLoading(false);
    }
  }

  return (
    <Box data-comp={ApplePay.displayName} sx={theme.wrapper}>
      <Box
        as="button"
        onClick={() => handleApplePay()}
        disabled={loading}
        aria-label="Buy with Apple Pay"
        className="apple-pay-button-with-text apple-pay-button-white-with-text"
        sx={{
          ...theme.button,
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.6 : 1,
        }}
      >
        <Box as="span" sx={theme.button.text} className="text">
          Buy with
        </Box>
        <Box as="span" sx={theme.button.logo} className="logo" />
      </Box>
    </Box>
  );
});

ApplePay.displayName = 'ApplePay';
